import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { ExclusiveFlag } from "scmp-app/components/content/content-item-render/exclusive-flag";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { homeV2FlexiUnitContentItemContent$key } from "scmp-app/queries/__generated__/homeV2FlexiUnitContentItemContent.graphql";

import {
  Container,
  CoverImage,
  ExclusiveLabelContainer,
  Headline,
  SectionLabel,
  StyledEntityLink,
  Summary,
  TextBlock,
  TextColumn,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  label: string;
  reference: homeV2FlexiUnitContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  label,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment homeV2FlexiUnitContentItemContent on Content {
        ...entityLink
        entityId
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withSummary: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withCoverImageSize1200x800: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content);

  const isExclusiveLabel = String(label).toLowerCase() === "exclusive";

  return (
    <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
      <Container className={className} ref={ref}>
        <TextColumn>
          <TextBlock>
            {isExclusiveLabel ? (
              <ExclusiveLabelContainer>
                <ExclusiveFlag />
              </ExclusiveLabelContainer>
            ) : (
              <SectionLabel>{label}</SectionLabel>
            )}
            <Headline>
              {providers.headline({ hideTag: true, hideTooltip: true, preferSocialHeadline: true })}
            </Headline>
            <Summary>{providers.summary()}</Summary>
          </TextBlock>
        </TextColumn>
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size1200x800",
              tabletUp: "size540x360",
            },
          })}
        </CoverImage>
      </Container>
    </StyledEntityLink>
  );
};

Component.displayName = "ContentItemHomeV2FlexiUnit";

export const ContentItemHomeV2FlexiUnit = withHighlightedHeadline(Component);
